import { useCallback, useRef, useState, type CSSProperties } from 'react';

import { formatDate } from '@/utils/date';

import { onActivateKey } from './activate';
import { Calendar } from './Calendar';
import { cx } from './classNames';
import { Portal } from './Portal';
import { useOutsideDismiss, usePopupPosition } from './usePopupPosition';

/**
 * Pendant zu `SfDateRangePicker`. Wird ausschließlich in der Grid-Filterzeile
 * benutzt; Anzeige `dd.MM.yyyy - dd.MM.yyyy`.
 */
export interface DateRangePickerProps {
  startDate: Date | null;
  endDate: Date | null;
  onChange: (range: { startDate: Date | null; endDate: Date | null }) => void;
  placeholder?: string;
  showClearButton?: boolean;
  disabled?: boolean;
  id?: string;
  className?: string;
  style?: CSSProperties;
  title?: string;
}

function formatRange(start: Date | null, end: Date | null): string {
  if (start === null && end === null) return '';
  return `${formatDate(start)} - ${formatDate(end)}`;
}

export function DateRangePicker(props: DateRangePickerProps) {
  const { onChange } = props;
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLSpanElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);

  const close = useCallback(() => {
    setOpen(false);
  }, []);
  useOutsideDismiss(open, close, [anchorRef, popupRef]);
  const popupStyle = usePopupPosition({ open, anchorRef, popupRef });

  const text = formatRange(props.startDate, props.endDate);
  const hasValue = props.startDate !== null || props.endDate !== null;

  const toggle = () => {
    if (props.disabled !== true) setOpen((value) => !value);
  };
  const clear = () => {
    onChange({ startDate: null, endDate: null });
  };

  // Der erste Klick setzt den Anfang, der zweite das Ende — wie bei Syncfusion.
  const select = (date: Date) => {
    if (props.startDate === null || props.endDate !== null) {
      onChange({ startDate: date, endDate: null });
      return;
    }
    if (date.getTime() < props.startDate.getTime()) {
      onChange({ startDate: date, endDate: props.startDate });
    } else {
      onChange({ startDate: props.startDate, endDate: date });
    }
    setOpen(false);
  };

  return (
    <>
      <span
        id={props.id}
        ref={anchorRef}
        title={props.title}
        style={props.style}
        className={cx(
          'e-input-group',
          'e-date-range-wrapper',
          open && 'e-dateinput-active',
          props.disabled === true && 'e-disabled',
          props.className,
        )}
      >
        {/* Das Feld ist schreibgeschützt und dient nur zum Öffnen; ohne
            Tastaturauslösung wäre der Bereichsfilter ohne Maus unerreichbar. */}
        <input
          type="text"
          className="e-input"
          value={text}
          readOnly
          placeholder={props.placeholder}
          disabled={props.disabled === true}
          onClick={toggle}
          onKeyDown={onActivateKey(toggle)}
        />
        {props.showClearButton === true ? (
          <span
            role="button"
            tabIndex={0}
            aria-label="Auswahl löschen"
            className={cx(
              'e-clear-icon',
              'e-icons',
              'fa-solid fa-xmark',
              !hasValue && 'e-clear-icon-hide',
            )}
            onClick={clear}
            onKeyDown={onActivateKey(clear)}
          />
        ) : null}
        <span
          role="button"
          tabIndex={0}
          aria-label="Kalender öffnen"
          aria-expanded={open}
          className="e-input-group-icon e-date-icon e-icons fa-solid fa-calendar-days"
          onClick={toggle}
          onKeyDown={onActivateKey(toggle)}
        />
      </span>
      {open ? (
        <Portal>
          {/* Lage und Ebene liefert `.e-popup` aus dem Stylesheet (REQ-15). */}
          <div ref={popupRef} className="e-popup" style={popupStyle}>
            <Calendar rangeStart={props.startDate} rangeEnd={props.endDate} onSelect={select} />
          </div>
        </Portal>
      ) : null}
    </>
  );
}

export default DateRangePicker;
